import { SPACING_TOKENS } from '../../constants/tokens.mjs';

const MIN_SPACING = SPACING_TOKENS['space-2'];
const MIN_SPACING_NUMBER = parseInt(MIN_SPACING, 10);
const MAX_HEIGHT_PERCENTAGE = 75;
function calculatePopoverTopPosition({
  anchorTopPosition,
  anchorHeight,
  containerHeight,
  popoverScrollHeight,
  popoverCustomHeight,
  verticalAlign
}) {
  // Calculates the top position for the popover to use the maximum allowed space
  // The popover height property is set based on the top, in the DEFAULT_OVERRIDES

  // Maximum potential popover height the container size allows
  // When verticalAlign is 'top', we prevent the popover from using
  // more than MAX_HEIGHT_PERCENTAGE of the container height
  // When verticalAlign is 'center', we allow the popover to use
  // the entire container height
  const maxUsableHeight = verticalAlign === 'top' ? containerHeight * (MAX_HEIGHT_PERCENTAGE / 100) : containerHeight;

  // Desirable popover height
  const popoverHeight = popoverCustomHeight || popoverScrollHeight;

  // Whether the popover can fit the screen when using the desirable height
  const popoverExceedsUsableHeight = popoverHeight >= maxUsableHeight;
  if (popoverExceedsUsableHeight) {
    const minTop = containerHeight - maxUsableHeight - MIN_SPACING_NUMBER;
    const anchorAboveMinTop = anchorTopPosition <= minTop;

    // If the anchor top is above the minimum top position, we use the anchor top
    // Otherwise we use the minimum top
    return Math.max(anchorAboveMinTop ? anchorTopPosition : minTop, MIN_SPACING_NUMBER);
  }
  switch (verticalAlign) {
    case 'center':
      {
        const offset = anchorHeight / 2 + popoverHeight / 2;

        // Whether the anchorTop is high enough for the popover to fit when vertically center-aligned
        const popoverFitsAtAnchorPosition = containerHeight - anchorTopPosition - MIN_SPACING_NUMBER >= popoverHeight - offset;
        const topPosition = popoverFitsAtAnchorPosition ? anchorTopPosition + anchorHeight / 2 - popoverHeight / 2 : containerHeight - popoverHeight - MIN_SPACING_NUMBER;
        return Math.max(topPosition, MIN_SPACING_NUMBER);
      }
    case 'top':
    default:
      {
        // Whether the anchorTop is high enough for the popover to fit entirely
        const popoverFitsAtAnchorPosition = containerHeight - anchorTopPosition - MIN_SPACING_NUMBER >= popoverHeight;
        const topPosition = popoverFitsAtAnchorPosition ? anchorTopPosition : containerHeight - popoverHeight - MIN_SPACING_NUMBER;
        return Math.max(topPosition, MIN_SPACING_NUMBER);
      }
  }
}
const calculatePopoverLeftPosition = (anchorLayout, popoverCustomWidth, scrollWidth) => {
  const gap = MIN_SPACING_NUMBER;
  const popoverWidth = popoverCustomWidth || scrollWidth;
  const showOnAnchorLeft = typeof anchorLayout.distanceRight !== 'undefined' && typeof popoverWidth !== 'undefined' && anchorLayout.distanceRight < popoverWidth + gap && anchorLayout.left > popoverWidth + gap;
  if (showOnAnchorLeft) {
    return anchorLayout.left - popoverWidth - gap;
  }
  return anchorLayout.right + gap;
};
function getPopoverLayout({
  anchorLayout,
  containerSize,
  scrollSize,
  options
}) {
  var _options$overrides$to, _options$overrides;
  if (!scrollSize || !(containerSize != null && containerSize.height)) {
    return anchorLayout;
  }
  const y = (_options$overrides$to = options == null ? void 0 : (_options$overrides = options.overrides) == null ? void 0 : _options$overrides.top) != null ? _options$overrides$to : calculatePopoverTopPosition({
    anchorTopPosition: anchorLayout.top,
    anchorHeight: anchorLayout.height,
    containerHeight: containerSize.height,
    popoverScrollHeight: scrollSize.height,
    popoverCustomHeight: options == null ? void 0 : options.height,
    verticalAlign: (options == null ? void 0 : options.verticalAlign) || 'top'
  });
  const x = calculatePopoverLeftPosition(anchorLayout, options == null ? void 0 : options.width, scrollSize.width);
  return {
    top: y,
    left: x,
    right: MIN_SPACING_NUMBER,
    height: options == null ? void 0 : options.height,
    width: options == null ? void 0 : options.width,
    ...(options != null && options.maxHeight ? {
      maxHeight: options.maxHeight
    } : {}),
    ...(options != null && options.maxWidth ? {
      maxWidth: options.maxWidth
    } : {})
  };
}

export { MAX_HEIGHT_PERCENTAGE, MIN_SPACING_NUMBER, calculatePopoverTopPosition, getPopoverLayout };
